import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, Shield, Settings, X, Check, ChevronRight, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCookieConsent } from '../context/CookieConsentContext'

export default function CookieConsentBanner() {
  const { showBanner, acceptAll, openPreferences } = useCookieConsent()

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="max-w-6xl mx-auto max-h-[85vh] overflow-y-auto">
            <div className="bg-void-900/95 backdrop-blur-xl rounded-2xl border border-void-700/50 shadow-2xl shadow-void-950/80 overflow-hidden">
              {/* Main Banner Content */}
              <div className="p-6 md:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Icon and Text */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-xl bg-synth-900/50 border border-synth-700/30">
                        <Cookie className="w-6 h-6 text-synth-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-display font-semibold text-void-50">
                          Your Privacy Matters
                        </h3>
                        <p className="text-xs text-void-500">
                          GDPR, CCPA & CPRA Compliant
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-void-300 text-sm leading-relaxed mb-4">
                      SynthetIQ Signals®, Inc. uses cookies and similar technologies to enhance
                      your experience, analyze site usage, and assist in our marketing efforts. Some cookies 
                      are essential for the operation of our services, while others help us improve your 
                      experience and enable personalized features.
                    </p>
                    
                    <p className="text-void-400 text-sm leading-relaxed mb-4">
                      In accordance with the <strong className="text-void-300">California Consumer Privacy Act (CCPA)</strong>, 
                      the <strong className="text-void-300">California Privacy Rights Act (CPRA)</strong>, 
                      the <strong className="text-void-300">General Data Protection Regulation (GDPR)</strong>, 
                      and other applicable privacy laws, you have the right to control how your personal 
                      information is collected and used by our applications and machine learning systems.
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-void-500">
                      <Link 
                        to="/corporate/legal/privacy" 
                        className="flex items-center gap-1 hover:text-synth-400 transition-colors"
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Privacy Policy
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <Link
                        to="/corporate/legal/cookies"
                        className="flex items-center gap-1 hover:text-synth-400 transition-colors"
                      >
                        Cookie Policy
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <span className="text-void-600">|</span>
                      <span>
                        We do not sell your personal information
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
                    <button
                      onClick={acceptAll}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-synth-600 to-synth-700 text-white font-display font-semibold rounded-xl hover:from-synth-500 hover:to-synth-600 transition-all duration-300 shadow-lg shadow-synth-900/50 hover:shadow-xl hover:shadow-synth-800/50"
                    >
                      <Check className="w-4 h-4" />
                      Accept All Cookies
                    </button>
                    
                    <button
                      onClick={openPreferences}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-void-800/50 text-void-200 font-display font-medium rounded-xl border border-void-700/50 hover:bg-void-700/50 hover:border-synth-600/30 hover:text-void-50 transition-all duration-300"
                    >
                      <Settings className="w-4 h-4" />
                      Customize Preferences
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Info Strip */}
              <div className="px-6 md:px-8 py-3 bg-void-950/50 border-t border-void-800/50">
                <div className="flex flex-wrap items-center justify-between gap-4 text-xs">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-void-400">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Essential cookies are always active
                    </span>
                  </div>
                  <button
                    onClick={openPreferences}
                    className="flex items-center gap-1 text-synth-400 hover:text-synth-300 transition-colors"
                  >
                    Learn about cookie categories
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}